import React, { useState, useEffect, useCallback } from 'react';
import FestivalGreeting from '../../components/festival/FestivalGreeting';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import {
  FolderOpen, CheckCircle, Clock, AlertTriangle, Users,
  ChevronRight, Activity, Package, Truck,
  Filter, ArrowRight, GitMerge, Layers, Search
} from 'lucide-react';

// ─── DISPATCH FLOW CARD ───────────────────────────────────────────────────────
// Today's numbers for each step, then what is still waiting (all dates):
// packed boxes not on a Sale Challan, and challans not delivered yet.
function DispatchFlowCard({ stats, navigate }) {
  const v = k => Number(stats?.[k]) || 0;
  const steps = [
    { label: 'Worker Done', value: v('dispatch_worker_qty'), sub: 'qty marked done', icon: Users, tone: 'emerald' },
    { label: 'Packed', value: v('packed_boxes_today'), sub: 'boxes packed', icon: Package, tone: 'indigo', to: '/admin/packing' },
    { label: 'On Sale Challan', value: v('boxes_on_challan_today'),
      sub: `boxes on ${v('challans_today')} challan${v('challans_today') === 1 ? '' : 's'}`, icon: Layers, tone: 'blue', to: '/admin/sale-challans' },
    { label: 'Delivered', value: v('delivered_today'), sub: `challan${v('delivered_today') === 1 ? '' : 's'} delivered`, icon: Truck, tone: 'green', to: '/admin/sale-challans' },
  ];
  const TONE = {
    emerald: ['bg-emerald-50', 'text-emerald-500', 'text-emerald-600'],
    indigo: ['bg-indigo-50', 'text-indigo-500', 'text-indigo-600'],
    blue: ['bg-blue-50', 'text-blue-500', 'text-blue-600'],
    green: ['bg-green-50', 'text-green-500', 'text-green-600'],
  };
  const notOnChallan = v('boxes_not_on_challan');
  const notDelivered = v('challans_not_delivered');
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4 space-y-3">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="flex items-center gap-3 min-w-0 lg:w-56 flex-shrink-0">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-[#0EA5E9]">
            <Truck className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-xs text-gray-400 font-medium">Dispatch Today</p>
            <p className="text-xs text-gray-300 leading-tight">Done → Packed → Challan → Delivered</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 flex-1 min-w-0 lg:border-l lg:border-gray-100 lg:pl-3">
          {steps.map((st, i) => {
            const [bg, ic, tx] = TONE[st.tone];
            const Icon = st.icon;
            return (
              <div key={st.label} onClick={st.to ? () => navigate(st.to) : undefined}
                className={`relative flex items-center gap-2.5 min-w-0 rounded-xl px-2 py-1.5 ${st.to ? 'cursor-pointer hover:bg-gray-50' : ''}`}>
                <div className={`w-9 h-9 rounded-xl ${bg} flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-4 h-4 ${ic}`} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-gray-400 font-medium truncate">{i + 1}. {st.label}</p>
                  <p className={`text-2xl font-black ${tx} leading-tight`}>{st.value}</p>
                  <p className="text-xs text-gray-400 truncate">{st.sub}</p>
                </div>
                {i < steps.length - 1 && <ChevronRight className="hidden md:block absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />}
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {notOnChallan > 0 && (
          <button onClick={() => navigate('/admin/packing')}
            className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-left hover:bg-amber-100">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
            <span>
              <span className="block text-xs font-bold text-amber-700">Challan Pending</span>
              <span className="block text-xs text-amber-600">{notOnChallan} packed box{notOnChallan === 1 ? '' : 'es'} not on any Sale Challan yet</span>
            </span>
          </button>
        )}
        {notDelivered > 0 && (
          <button onClick={() => navigate('/admin/sale-challans')}
            className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-xl px-3 py-2 text-left hover:bg-orange-100">
            <Clock className="w-4 h-4 text-orange-500 flex-shrink-0" />
            <span>
              <span className="block text-xs font-bold text-orange-700">Delivery Pending</span>
              <span className="block text-xs text-orange-600">{notDelivered} Sale Challan{notDelivered === 1 ? '' : 's'} not marked Delivered</span>
            </span>
          </button>
        )}
        {!notOnChallan && !notDelivered && v('boxes_total') > 0 && (
          <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-3 py-2">
            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
            <p className="text-xs font-bold text-green-700">All packed boxes are on challans and delivered</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── UTILS ────────────────────────────────────────────────────────────────────
const n   = v => Number(v) || 0;
const pct = (a, b) => n(b) > 0 ? Math.round((n(a) / n(b)) * 100) : 0;
const fmt = d => {
  try { return d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'; }
  catch { return '—'; }
};
const todayStr = () => new Date().toISOString().split('T')[0];
const PROJ_COLORS = ['#3B82F6','#10B981','#F59E0B','#8B5CF6','#06B6D4','#EC4899','#14B8A6','#F97316','#6366F1','#84CC16'];

// ─── BAR ──────────────────────────────────────────────────────────────────────
function Bar({ value, color = '#10B981', h = 6 }) {
  return (
    <div className="w-full rounded-full overflow-hidden bg-gray-100" style={{ height: h }}>
      <div className="h-full rounded-full transition-all duration-700"
        style={{ width: `${Math.min(Math.max(n(value), 0), 100)}%`, background: color }} />
    </div>
  );
}

// ─── STAT CARD ────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, icon: Icon, iconBg, onClick }) {
  return (
    <div onClick={onClick}
      className={`bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3 ${onClick ? 'cursor-pointer hover:shadow-md' : ''} transition-shadow`}>
      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: iconBg }}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-400 font-medium truncate leading-tight">{label}</p>
        <p className="text-2xl font-black text-gray-900 leading-tight">{value ?? 0}</p>
        {sub && <p className="text-xs text-gray-400 leading-tight">{sub}</p>}
      </div>
    </div>
  );
}

// ─── VIEW ALL BUTTON ──────────────────────────────────────────────────────────
function VA({ onClick, color = '#3B82F6' }) {
  return (
    <button onClick={onClick}
      className="flex items-center gap-1 text-xs font-semibold hover:opacity-70 transition-opacity"
      style={{ color }}>
      View All <ChevronRight className="w-3.5 h-3.5" />
    </button>
  );
}

// ─── EMPTY STATE ──────────────────────────────────────────────────────────────
function Empty({ icon: Icon, text }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-center">
      <Icon className="w-10 h-10 text-gray-200 mb-2" />
      <p className="text-gray-300 text-sm">{text}</p>
    </div>
  );
}

// ─── SPINNER ──────────────────────────────────────────────────────────────────
function Spinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="w-7 h-7 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// ─── SECTION DATE FILTER ──────────────────────────────────────────────────────
// Shortcuts only update the pending inputs — Apply button still required
function SectionDateFilter({ from, to, onFromChange, onToChange, onApply, loading, accentColor = '#3B82F6' }) {
  const setToday = () => {
    const t = todayStr();
    onFromChange(t);
    onToChange(t);
  };
  const setThisWeek = () => {
    const d = new Date(), day = d.getDay() || 7;
    const mon = new Date(d);
    mon.setDate(d.getDate() - day + 1);
    onFromChange(mon.toISOString().split('T')[0]);
    onToChange(todayStr());
  };
  const setThisMonth = () => {
    const d = new Date();
    onFromChange(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`);
    onToChange(todayStr());
  };
  return (
    <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-b border-gray-100 bg-gray-50/50">
      <Filter className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
      <div className="flex items-center gap-1.5">
        <label className="text-[11px] text-gray-400 whitespace-nowrap font-medium">From</label>
        <input type="date" value={from} onChange={e => onFromChange(e.target.value)}
          className="border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200 bg-white" />
      </div>
      <div className="flex items-center gap-1.5">
        <label className="text-[11px] text-gray-400 whitespace-nowrap font-medium">To</label>
        <input type="date" value={to} onChange={e => onToChange(e.target.value)}
          className="border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200 bg-white" />
      </div>
      <button
        onClick={onApply}
        disabled={loading}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white shadow-sm disabled:opacity-50 transition-all hover:opacity-90 active:scale-95"
        style={{ background: accentColor }}>
        {loading
          ? <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> Loading…</>
          : <><Search className="w-3 h-3" /> Apply Filter</>}
      </button>
      <div className="flex gap-1.5 ml-auto flex-wrap">
        {[['Today', setToday], ['This Week', setThisWeek], ['This Month', setThisMonth]].map(([lbl, fn]) => (
          <button key={lbl} onClick={fn}
            className="text-[11px] font-semibold px-2.5 py-1 rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-600 text-gray-400 transition-colors whitespace-nowrap bg-white">
            {lbl}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── useSectionData HOOK ──────────────────────────────────────────────────────
// - Auto-loads on mount with today's date
// - Date input changes only update pending values (no auto-fetch)
// - Apply button triggers fetch with current pending values
function useSectionData(field, seed) {
  const today = todayStr();
  const [pendingFrom, setPendingFrom] = useState(today);
  const [pendingTo,   setPendingTo]   = useState(today);
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback((f, t) => {
    setLoading(true);
    api.get(`/reports/dashboard?from=${f}&to=${t}`)
      .then(r => { if (r.data && !r.data.message) setData(r.data[field] || []); else setData([]); })
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [field]);

  // Seed today's data from the single shared dashboard payload (loaded once by
  // the parent). No separate per-section mount fetch — that made the dashboard
  // hit the same heavy endpoint 4x with identical params. We only re-fetch this
  // section when the user changes ITS date range and clicks Apply.
  useEffect(() => {
    if (seed !== undefined && seed !== null) setData(seed);
  }, [seed]);

  // Apply button — fetch with whatever dates user has set
  const apply = useCallback(() => {
    fetchData(pendingFrom, pendingTo);
  }, [pendingFrom, pendingTo, fetchData]);

  return { data, loading, pendingFrom, pendingTo, setPendingFrom, setPendingTo, apply };
}

// ─── MAIN DASHBOARD ───────────────────────────────────────────────────────────
export default function AdminDashboard() {
  const navigate = useNavigate();

  // Stat cards + items waiting + outsource — loaded once on mount (no date filter)
  const [stats,        setStats]        = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    api.get('/reports/dashboard')
      .then(r => { if (r.data && !r.data.message) setStats(r.data); })
      .catch(err => console.error('Dashboard stats error:', err))
      .finally(() => setStatsLoading(false));
  }, []);

  // Sections with independent date filters — seeded from the single stats
  // payload above (which already contains today's data for every section).
  const projOverview   = useSectionData('projectOverview',    stats?.projectOverview);
  const stageFlow      = useSectionData('stageFlowSummary',   stats?.stageFlowSummary);
  const workerReport   = useSectionData('workerDailyReport',  stats?.workerDailyReport);
  const dispatchReport = useSectionData('dispatchDailyReport', stats?.dispatchDailyReport);

  // Derived data
  const { projects = {}, extraStats = {} } = stats || {};
  const itemsWaiting24h    = (stats && stats.itemsWaiting24h)   || [];
  const outsourcePreview   = ((stats && stats.outsourcePreview) || []).slice(0, 10);
  const projectOverview    = projOverview.data    || [];
  const stageFlowSummary   = stageFlow.data       || [];
  const workerDailyReport  = workerReport.data    || [];
  const dispatchDailyReport = dispatchReport.data || [];

  const effColor = p => p >= 75 ? '#10B981' : p >= 40 ? '#F59E0B' : p > 0 ? '#EF4444' : '#CBD5E1';

  if (statsLoading) return (
    <div className="flex items-center justify-center" style={{ minHeight: 320 }}>
      <div className="text-center space-y-3">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-gray-400 text-sm">Loading dashboard…</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <FestivalGreeting />

      {/* ── STAT CARDS ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard label="Total Projects"      value={n(projects.total)}               sub="All Projects" icon={FolderOpen}    iconBg="#3B82F6" onClick={() => navigate('/admin/projects')} />
        <StatCard label="Running Projects"    value={n(projects.active)}              sub="Active"       icon={Activity}      iconBg="#10B981" onClick={() => navigate('/admin/projects?status=active')} />
        <StatCard label="Delayed Projects"    value={n(projects.overdue)}             sub="Overdue"      icon={AlertTriangle} iconBg="#F59E0B" onClick={() => navigate('/admin/projects')} />
        <StatCard label="Completed Projects"  value={n(projects.completed)}           sub="Done"         icon={CheckCircle}   iconBg="#22C55E" onClick={() => navigate('/admin/projects?status=completed')} />
        <StatCard label="Total Items Running" value={n(extraStats.items_running)}     sub="Live Items"   icon={Package}       iconBg="#6366F1" />
        <StatCard label="Pending Approvals"   value={n(extraStats.pending_approvals)} sub="To Approve"   icon={Clock}         iconBg="#F97316" onClick={() => navigate('/admin/approvals')} />
      </div>

      {/* ── DISPATCH TODAY CARD — Worker Done → Packing → Sale Challan → Delivered ── */}
      <DispatchFlowCard stats={extraStats} navigate={navigate} />

      {/* ══ SECTION 1 — PROJECT OVERVIEW (date filter, auto-loads today) ══ */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
          <h3 className="font-bold text-gray-800 text-[13px] uppercase tracking-wide">Project Overview</h3>
          <VA onClick={() => navigate('/admin/dashboard/project-overview')} />
        </div>
        <SectionDateFilter
          from={projOverview.pendingFrom} to={projOverview.pendingTo}
          onFromChange={projOverview.setPendingFrom} onToChange={projOverview.setPendingTo}
          onApply={projOverview.apply} loading={projOverview.loading}
          accentColor="#3B82F6"
        />
        {projOverview.loading ? <Spinner /> : projectOverview.length === 0 ? (
          <Empty icon={FolderOpen} text="No project activity in this date range" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/60">
                  {['Project Name','Client','Progress','Current Stage','Due Date','Days Left','Status','Health'].map(h => (
                    <th key={h} className="text-left text-[11px] font-semibold text-gray-400 uppercase px-4 py-3 whitespace-nowrap tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {projectOverview.map((p, idx) => {
                  const progress = pct(p.done_tasks, p.total_tasks);
                  const daysLeft = p.days_left !== null ? Number(p.days_left) : null;
                  const overdue  = daysLeft !== null && daysLeft < 0;
                  const ot       = n(p.overdue_tasks);
                  const statusLbl = (overdue || ot > 0) ? 'Delayed' : 'Running';
                  const health    = (overdue || ot >= 3) ? 'High' : (ot >= 1 || (daysLeft !== null && daysLeft <= 6)) ? 'Medium' : 'Low';
                  const barColor  = overdue ? '#EF4444' : PROJ_COLORS[idx % PROJ_COLORS.length];
                  return (
                    <tr key={p.id} className="hover:bg-blue-50/20 cursor-pointer transition-colors"
                      onClick={() => navigate(`/admin/projects/${p.id}`)}>
                      <td className="px-4 py-3 font-semibold text-gray-900 text-sm whitespace-nowrap">{p.name}</td>
                      <td className="px-4 py-3 text-gray-500 text-sm whitespace-nowrap">{p.client_name || '—'}</td>
                      <td className="px-4 py-3 min-w-[130px]">
                        <div className="flex items-center gap-2">
                          <div className="flex-1"><Bar value={progress} color={barColor} h={5} /></div>
                          <span className="text-xs font-bold w-8 text-right flex-shrink-0" style={{ color: barColor }}>{progress}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{p.current_stage || '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{fmt(p.deadline)}</td>
                      <td className="px-4 py-3 font-bold text-sm whitespace-nowrap"
                        style={{ color: daysLeft === null ? '#9CA3AF' : overdue ? '#EF4444' : daysLeft <= 7 ? '#F59E0B' : '#22C55E' }}>
                        {daysLeft === null ? '—' : daysLeft}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex text-xs font-semibold px-2.5 py-0.5 rounded-full ${statusLbl === 'Running' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                          {statusLbl}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex text-xs font-semibold px-2.5 py-0.5 rounded-full ${health === 'Low' ? 'bg-green-100 text-green-700' : health === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-600'}`}>
                          {health}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ══ SECTION 2 — ITEMS WAITING > 24H (NO date filter, always live) ══ */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
          <h3 className="font-bold text-gray-800 text-[13px] uppercase tracking-wide">Items Waiting More Than 24 Hours</h3>
          <VA onClick={() => navigate('/admin/dashboard/items-waiting')} />
        </div>
        {statsLoading ? <Spinner /> : itemsWaiting24h.length === 0 ? (
          <Empty icon={CheckCircle} text="No items stalled over 24 hours" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px]">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/60">
                  {['Item Code','Item Name','Project','Current Stage','Last Work Stage','Worker','Last Work At','Idle'].map(h => (
                    <th key={h} className="text-left text-[11px] font-semibold text-gray-400 uppercase px-3 py-2.5 whitespace-nowrap tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {itemsWaiting24h.map(item => {
                  const hrs  = n(item.hours_pending);
                  const days = Math.floor(hrs / 24);
                  const rh   = hrs % 24;
                  const lbl  = days > 0 ? `${days}d ${rh > 0 ? rh + 'h' : ''}`.trim() : `${hrs}h`;
                  return (
                    <tr key={item.id} className="hover:bg-red-50/20 cursor-pointer transition-colors"
                      onClick={() => navigate(`/admin/projects/${item.project_db_id}`)}>
                      <td className="px-3 py-2.5 font-mono text-xs text-gray-400">{item.item_code || '—'}</td>
                      <td className="px-3 py-2.5 font-semibold text-gray-800 text-sm whitespace-nowrap">{item.item_name || item.task_title}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">{item.project_name}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">{item.current_stage || '—'}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">{item.last_work_stage || '— (not started)'}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">{item.worker_name || '—'}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">
                        {item.pending_since
                          ? new Date(item.pending_since).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
                          : 'No work yet'}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`text-xs font-bold ${hrs >= 48 ? 'text-red-500' : 'text-orange-500'}`}>{lbl}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ══ SECTION 3 — OUTSOURCE OVERVIEW (NO date filter, pending/overdue only, max 10) ══ */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
          <h3 className="font-bold text-gray-800 text-[13px] uppercase tracking-wide">Outsource Overview</h3>
          <VA onClick={() => navigate('/admin/outsource')} />
        </div>
        {statsLoading ? <Spinner /> : outsourcePreview.length === 0 ? (
          <Empty icon={Truck} text="No pending or overdue outsource jobs" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/60">
                  {['S.No','Outsource Name','Order Placed Date','Due Date','Proto Code','Project Name','Days Until Due','Status'].map(h => (
                    <th key={h} className="text-left text-[11px] font-semibold text-gray-400 uppercase px-3 py-2.5 whitespace-nowrap tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {outsourcePreview.map((job, idx) => {
                  const jobDate   = job.expected_date ? job.expected_date.split('T')[0] : null;
                  const isOverdue = job.urgency === 'overdue' || (jobDate && jobDate < todayStr());
                  const isNext24h = !isOverdue && job.urgency === 'next_24h';
                  const statusLbl = isOverdue ? 'Overdue' : isNext24h ? 'Due Soon' : 'Pending';
                  const statusCls = isOverdue ? 'bg-red-100 text-red-600' : isNext24h ? 'bg-orange-100 text-orange-600' : 'bg-amber-100 text-amber-700';
                  const daysUntil = job.days_until_due !== null && job.days_until_due !== undefined ? Number(job.days_until_due) : null;
                  const daysLabel = daysUntil === null ? '—' : isOverdue ? `${Math.abs(daysUntil)}d ago` : `${daysUntil}d`;
                  const daysColor = isOverdue ? 'text-red-600 font-bold' : isNext24h ? 'text-orange-500 font-bold' : 'text-gray-700';
                  return (
                    <tr key={job.id} className="hover:bg-amber-50/20 cursor-pointer transition-colors"
                      onClick={() => navigate('/admin/outsource')}>
                      <td className="px-3 py-2.5 text-xs text-gray-400 font-semibold">{idx + 1}</td>
                      <td className="px-3 py-2.5 font-semibold text-gray-800 text-sm whitespace-nowrap">{job.vendor_name}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">{fmt(job.order_placed_date)}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-700 whitespace-nowrap font-medium">{fmt(job.expected_date)}</td>
                      <td className="px-3 py-2.5 font-mono text-xs text-gray-600 whitespace-nowrap">{job.proto_code || '—'}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">{job.project_name || '—'}</td>
                      <td className={`px-3 py-2.5 text-sm whitespace-nowrap ${daysColor}`}>{daysLabel}</td>
                      <td className="px-3 py-2.5">
                        <span className={`inline-flex text-xs font-semibold px-2.5 py-0.5 rounded-full ${statusCls}`}>{statusLbl}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ══ SECTION 4 — STAGE FLOW SUMMARY (date filter, auto-loads today) ══ */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
          <div className="flex items-center gap-4">
            <h3 className="font-bold text-gray-800 text-[13px] uppercase tracking-wide">
              Stage Flow Summary
              <span className="font-normal text-gray-400 normal-case text-xs ml-1">(All Projects)</span>
            </h3>
            {[['#22C55E','On Track'],['#F59E0B','Attention'],['#EF4444','Delayed']].map(([c, lbl]) => (
              <div key={lbl} className="hidden sm:flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ background: c }} />
                <span className="text-xs text-gray-500">{lbl}</span>
              </div>
            ))}
          </div>
        </div>
        <SectionDateFilter
          from={stageFlow.pendingFrom} to={stageFlow.pendingTo}
          onFromChange={stageFlow.setPendingFrom} onToChange={stageFlow.setPendingTo}
          onApply={stageFlow.apply} loading={stageFlow.loading}
          accentColor="#8B5CF6"
        />
        {stageFlow.loading ? <Spinner /> : stageFlowSummary.length === 0 ? (
          <div className="p-5"><Empty icon={GitMerge} text="No stage data in this range" /></div>
        ) : (
          <div className="p-5 overflow-x-auto">
            <div className="flex items-stretch gap-1 min-w-max">
              {stageFlowSummary.map((stage, idx) => {
                const totalWorking = n(stage.total_working);
                const todayIn      = n(stage.today_in);
                const todayOut     = n(stage.today_out);
                const pendingCount = n(stage.pending_count);
                const inProgCount  = n(stage.in_progress_count);
                const closingQty   = Math.max(0, totalWorking + todayIn - todayOut);
                const sColor = inProgCount > 0 && todayOut === 0 ? '#F59E0B'
                  : pendingCount > 10 ? '#EF4444'
                  : totalWorking > 0 ? '#22C55E' : '#CBD5E1';
                return (
                  <React.Fragment key={stage.id}>
                    <div className="flex flex-col p-3 rounded-2xl border-2 min-w-[148px]"
                      style={{ borderColor: sColor + '30', background: sColor + '06' }}>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-xs font-black flex-shrink-0"
                          style={{ background: stage.color || sColor }}>
                          {(stage.name || '').charAt(0).toUpperCase()}
                        </div>
                        <p className="font-bold text-gray-800 text-xs leading-tight">{stage.name}</p>
                      </div>
                      <div className="space-y-1.5 text-xs">
                        <div className="flex justify-between items-center py-1 border-b border-gray-100">
                          <span className="text-gray-500 font-medium">Total Working</span>
                          <span className="font-black text-gray-800">{totalWorking}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sky-500 font-medium">↓ In</span>
                          <span className="font-bold text-sky-600">{todayIn > 0 ? todayIn : '—'}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-emerald-500 font-medium">↑ Out</span>
                          <span className="font-bold text-emerald-600">{todayOut > 0 ? todayOut : '—'}</span>
                        </div>
                        <div className="flex justify-between items-center py-1 border-t border-gray-100">
                          <span className="text-indigo-500 font-medium">Closing</span>
                          <span className="font-black text-indigo-700">{closingQty}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-amber-500 font-medium">Pending</span>
                          <span className={`font-bold ${pendingCount > 0 ? 'text-amber-600' : 'text-gray-300'}`}>
                            {pendingCount > 0 ? pendingCount : '—'}
                          </span>
                        </div>
                      </div>
                    </div>
                    {idx < stageFlowSummary.length - 1 && (
                      <div className="flex items-center flex-shrink-0">
                        <ArrowRight className="w-4 h-4 text-gray-200" />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ══ SECTION 5 — WORKER DAILY REPORT (date filter, auto-loads today) ══ */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
          <div>
            <h3 className="font-bold text-gray-800 text-[13px] uppercase tracking-wide">Worker Daily Report</h3>
            <p className="text-xs text-gray-400 mt-0.5">Opening → In → Out → Closing · per worker</p>
          </div>
          <VA onClick={() => navigate('/admin/workers')} />
        </div>
        <SectionDateFilter
          from={workerReport.pendingFrom} to={workerReport.pendingTo}
          onFromChange={workerReport.setPendingFrom} onToChange={workerReport.setPendingTo}
          onApply={workerReport.apply} loading={workerReport.loading}
          accentColor="#10B981"
        />
        {workerReport.loading ? <Spinner /> : workerDailyReport.length === 0 ? (
          <Empty icon={Users} text="No worker activity in this range" />
        ) : (() => {
          const workerMap = {};
          workerDailyReport.forEach(w => {
            if (!workerMap[w.worker_id]) workerMap[w.worker_id] = { ...w, depts: [] };
            const opening  = n(w.opening_qty);
            const todayIn  = n(w.today_in);
            const todayOut = n(w.today_out);
            const closing  = Math.max(0, opening + todayIn - todayOut);
            const base     = opening + todayIn;
            const effPct   = base > 0 ? Math.min(Math.round((todayOut / base) * 100), 100) : 0;
            workerMap[w.worker_id].depts.push({ ...w, opening, todayIn, todayOut, closing, base, effPct });
          });
          const workers = Object.values(workerMap).map(wk => {
            const totOpen  = wk.depts.reduce((s, d) => s + d.opening, 0);
            const totIn    = wk.depts.reduce((s, d) => s + d.todayIn, 0);
            const totOut   = wk.depts.reduce((s, d) => s + d.todayOut, 0);
            const totClose = Math.max(0, totOpen + totIn - totOut);
            const totBase  = totOpen + totIn;
            const totEff   = totBase > 0 ? Math.min(Math.round((totOut / totBase) * 100), 100) : 0;
            const totTasks  = wk.depts.reduce((s, d) => s + n(d.total_tasks), 0);
            const doneTasks = wk.depts.reduce((s, d) => s + n(d.completed_tasks), 0);
            const actTasks  = wk.depts.reduce((s, d) => s + n(d.active_tasks), 0);
            return { ...wk, totOpen, totIn, totOut, totClose, totBase, totEff, totTasks, doneTasks, actTasks };
          });
          return (
            <div>
              {/* Worker summary cards */}
              <div className="overflow-x-auto border-b border-gray-100">
                <div className="flex gap-0 min-w-max">
                  {workers.map((wk, idx) => {
                    const c = effColor(wk.totEff);
                    return (
                      <div key={wk.worker_id}
                        className="flex flex-col items-center px-5 py-4 min-w-[160px] border-r border-gray-100"
                        style={{ background: idx % 2 === 0 ? '#FAFAFA' : '#FFFFFF' }}>
                        <div className="relative w-16 h-16 mb-2 flex-shrink-0">
                          <svg width="64" height="64" style={{ transform: 'rotate(-90deg)' }}>
                            <circle cx="32" cy="32" r="24" fill="none" stroke="#F1F5F9" strokeWidth="8" />
                            <circle cx="32" cy="32" r="24" fill="none" stroke={c} strokeWidth="8"
                              strokeDasharray={`${(wk.totEff / 100) * 150.8} 150.8`} strokeLinecap="butt" />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-xs font-black" style={{ color: c }}>{wk.totEff}%</span>
                          </div>
                        </div>
                        <p className="font-bold text-gray-800 text-xs text-center leading-tight mb-0.5 truncate w-full">{wk.worker_name}</p>
                        <div className="flex flex-wrap gap-1 justify-center mb-2">
                          {wk.depts.slice(0, 2).map(d => (
                            <span key={d.dept_id} className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                              style={{ background: (d.dept_color || '#9CA3AF') + '20', color: d.dept_color || '#6B7280' }}>
                              {d.dept_name}
                            </span>
                          ))}
                          {wk.depts.length > 2 && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                              +{wk.depts.length - 2}
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] font-bold text-gray-600">{wk.doneTasks}/{wk.totTasks} tasks</p>
                        <p className="text-[10px] text-gray-400 mb-2">
                          {wk.actTasks > 0 ? <span className="text-amber-500 font-semibold">{wk.actTasks} active</span> : 'On track'}
                        </p>
                        <div className="w-full text-[10px] space-y-0.5">
                          <div className="flex justify-between"><span className="text-gray-400">Opening</span><span className="font-bold text-gray-700">{wk.totOpen || '—'}</span></div>
                          <div className="flex justify-between"><span className="text-sky-400">↓ In</span><span className="font-bold text-sky-600">{wk.totIn || '—'}</span></div>
                          <div className="flex justify-between"><span className="text-emerald-400">↑ Out</span><span className="font-bold text-emerald-600">{wk.totOut || '—'}</span></div>
                          <div className="flex justify-between border-t border-gray-100 pt-0.5 mt-0.5">
                            <span className="text-indigo-400 font-semibold">Closing</span>
                            <span className="font-black text-indigo-700">{wk.totClose || '—'}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Dept breakdown table */}
              <div className="overflow-x-auto">
                <table className="w-full" style={{ minWidth: `${Math.max(700, workers.length * 200)}px` }}>
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50/60">
                      <th className="text-left text-[11px] font-semibold text-gray-400 uppercase px-4 py-2.5 whitespace-nowrap sticky left-0 bg-gray-50/60 z-10 w-32">Department</th>
                      {workers.map(wk => (
                        <th key={wk.worker_id} colSpan={5}
                          className="text-center text-[11px] font-bold text-gray-700 uppercase px-2 py-2.5 border-l border-gray-100 whitespace-nowrap">
                          {wk.worker_name}
                        </th>
                      ))}
                    </tr>
                    <tr className="border-b border-gray-200 bg-gray-50/40">
                      <th className="sticky left-0 bg-gray-50/40 z-10 px-4 py-1.5" />
                      {workers.map(wk => (
                        <React.Fragment key={wk.worker_id}>
                          {['Open','In','Out','Close','Eff'].map(h => (
                            <th key={h} className="text-center text-[10px] font-semibold uppercase px-2 py-1.5 whitespace-nowrap border-l border-gray-100"
                              style={{ color: h==='In'?'#0EA5E9':h==='Out'?'#10B981':h==='Close'?'#6366F1':h==='Eff'?'#F59E0B':'#9CA3AF' }}>
                              {h}
                            </th>
                          ))}
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {[...new Set(workerDailyReport.map(w => w.dept_name))].map(deptName => (
                      <tr key={deptName} className="hover:bg-indigo-50/10 transition-colors">
                        <td className="px-4 py-2.5 sticky left-0 bg-white z-10 border-r border-gray-100">
                          <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">{deptName}</span>
                        </td>
                        {workers.map(wk => {
                          const dept = wk.depts.find(d => d.dept_name === deptName);
                          const c    = dept ? effColor(dept.effPct) : '#CBD5E1';
                          return (
                            <React.Fragment key={wk.worker_id}>
                              <td className="px-2 py-2.5 text-center border-l border-gray-100">
                                <span className="text-xs font-bold text-gray-600">{dept && dept.opening > 0 ? dept.opening : '—'}</span>
                              </td>
                              <td className="px-2 py-2.5 text-center">
                                <span className={`text-xs font-bold ${dept && dept.todayIn > 0 ? 'text-sky-600' : 'text-gray-200'}`}>{dept && dept.todayIn > 0 ? dept.todayIn : '—'}</span>
                              </td>
                              <td className="px-2 py-2.5 text-center">
                                <span className={`text-xs font-bold ${dept && dept.todayOut > 0 ? 'text-emerald-600' : 'text-gray-200'}`}>{dept && dept.todayOut > 0 ? dept.todayOut : '—'}</span>
                              </td>
                              <td className="px-2 py-2.5 text-center">
                                <span className={`text-xs font-black ${dept && dept.closing > 0 ? 'text-indigo-700' : 'text-gray-200'}`}>{dept && dept.closing > 0 ? dept.closing : '—'}</span>
                              </td>
                              <td className="px-2 py-2.5 text-center">
                                {dept && dept.base > 0
                                  ? <span className="text-xs font-black" style={{ color: c }}>{dept.effPct}%</span>
                                  : <span className="text-xs text-gray-200">—</span>}
                              </td>
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    ))}
                    <tr className="bg-gray-50/80 border-t-2 border-gray-200">
                      <td className="px-4 py-2.5 sticky left-0 bg-gray-50/80 z-10 border-r border-gray-200">
                        <span className="text-xs font-black text-gray-800 uppercase">Total</span>
                      </td>
                      {workers.map(wk => {
                        const c = effColor(wk.totEff);
                        return (
                          <React.Fragment key={wk.worker_id}>
                            <td className="px-2 py-2.5 text-center border-l border-gray-200"><span className="text-xs font-black text-gray-700">{wk.totOpen || '—'}</span></td>
                            <td className="px-2 py-2.5 text-center"><span className="text-xs font-black text-sky-600">{wk.totIn || '—'}</span></td>
                            <td className="px-2 py-2.5 text-center"><span className="text-xs font-black text-emerald-600">{wk.totOut || '—'}</span></td>
                            <td className="px-2 py-2.5 text-center"><span className="text-xs font-black text-indigo-700">{wk.totClose || '—'}</span></td>
                            <td className="px-2 py-2.5 text-center"><span className="text-xs font-black" style={{ color: c }}>{wk.totEff}%</span></td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}
      </div>

      {/* ══ SECTION 6 — DISPATCH DAILY REPORT (date filter, auto-loads today) ══ */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
          <div>
            <h3 className="font-bold text-gray-800 text-[13px] uppercase tracking-wide">Dispatch Daily Report</h3>
            <p className="text-xs text-gray-400 mt-0.5">Per project · Opening (in dispatch) + In (approved today) − Out (dispatched today) = Closing</p>
          </div>
          <div className="flex items-center gap-3">
            {n(extraStats.dispatch_pending_qty) > 0 && (
              <div className="text-right">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide">Total Pending</p>
                <p className="text-lg font-black text-indigo-700 leading-tight">{n(extraStats.dispatch_pending_qty)}</p>
              </div>
            )}
            <VA onClick={() => navigate('/admin/dashboard/dispatch-report')} />
          </div>
        </div>
        <SectionDateFilter
          from={dispatchReport.pendingFrom} to={dispatchReport.pendingTo}
          onFromChange={dispatchReport.setPendingFrom} onToChange={dispatchReport.setPendingTo}
          onApply={dispatchReport.apply} loading={dispatchReport.loading}
          accentColor="#0EA5E9"
        />
        {dispatchReport.loading ? <Spinner /> : dispatchDailyReport.length === 0 ? (
          <Empty icon={Truck} text="No dispatch activity in this range" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/60">
                  {['S.No','Project','Project Code','Items','Opening','In','Out','Closing','Status'].map(h => (
                    <th key={h} className="text-left text-[11px] font-semibold text-gray-400 uppercase px-3 py-2.5 whitespace-nowrap tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {dispatchDailyReport.map((row, idx) => {
                  const opening  = n(row.opening_qty);
                  const todayIn  = n(row.today_in);
                  const todayOut = n(row.today_out);
                  const closing  = Math.max(0, opening + todayIn - todayOut);
                  const fullyDispatched = closing === 0 && (opening + todayIn) > 0;
                  const hasChallanGap   = todayOut === 0 && (opening + todayIn) > 0;
                  return (
                    <tr key={row.project_id} className="hover:bg-blue-50/20 cursor-pointer transition-colors"
                      onClick={() => navigate(`/admin/projects/${row.project_id}`)}>
                      <td className="px-3 py-2.5 text-xs text-gray-400 font-semibold">{idx + 1}</td>
                      <td className="px-3 py-2.5 font-semibold text-gray-800 text-sm whitespace-nowrap">{row.project_name}</td>
                      <td className="px-3 py-2.5 font-mono text-xs text-gray-500">{row.proj_code || '—'}</td>
                      <td className="px-3 py-2.5 text-center text-xs font-semibold text-gray-600">{n(row.item_count) || '—'}</td>
                      <td className="px-3 py-2.5 text-center font-bold text-gray-700">{opening > 0 ? opening : '—'}</td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`text-sm font-bold ${todayIn > 0 ? 'text-sky-600' : 'text-gray-300'}`}>{todayIn > 0 ? `↓ ${todayIn}` : '—'}</span>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`text-sm font-bold ${todayOut > 0 ? 'text-emerald-600' : 'text-gray-300'}`}>{todayOut > 0 ? `↑ ${todayOut}` : '—'}</span>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`text-sm font-black ${closing > 0 ? 'text-indigo-700' : 'text-gray-300'}`}>{closing > 0 ? closing : '—'}</span>
                      </td>
                      <td className="px-3 py-2.5">
                        {fullyDispatched
                          ? <span className="inline-flex text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">✓ Dispatched</span>
                          : hasChallanGap
                            ? <span className="inline-flex text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Pending Dispatch</span>
                            : <span className="inline-flex text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">In Progress</span>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
}
